import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    CircularProgress,
    Paper,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Button,
    Chip,
    Avatar,
    Dialog,
    IconButton,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Person as PersonIcon,
    Close as CloseIcon,
    LocalOffer as TagIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import contentService from '../services/contentService';
import ContentCard from '../components/ContentCard';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import '../styles/ProtectedRoutes.css';

const AdminRequests = () => {
    const [pendingPosts, setPendingPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPost, setSelectedPost] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const loadPendingPosts = async () => {
        try {
            setLoading(true);
            const response = await contentService.getPendingPosts(100, 0);
            
            if (response.success) {
                setPendingPosts(response.data);
            } else {
                toast.error('Failed to load pending posts');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to load pending posts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPendingPosts();
    }, []);

    const handleApprove = async (contentId) => {
        try {
            setActionLoading(contentId);
            const response = await contentService.approvePost(contentId);
            
            if (response.success) {
                toast.success('Post approved successfully!');
                // Remove from pending list
                setPendingPosts(prev => prev.filter(post => post.ContentId !== contentId));
                if (selectedPost && selectedPost.ContentId === contentId) {
                    setModalOpen(false);
                    setSelectedPost(null);
                }
            } else {
                toast.error(response.message || 'Failed to approve post');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to approve post');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (contentId) => {
        try {
            setActionLoading(contentId);
            const response = await contentService.rejectPost(contentId);
            
            if (response.success) {
                toast.success('Post rejected');
                // Remove from pending list
                setPendingPosts(prev => prev.filter(post => post.ContentId !== contentId));
                if (selectedPost && selectedPost.ContentId === contentId) {
                    setModalOpen(false);
                    setSelectedPost(null);
                }
            } else {
                toast.error(response.message || 'Failed to reject post');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to reject post');
        } finally {
            setActionLoading(null);
        }
    };

    const handlePostClick = (post) => {
        setSelectedPost(post);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedPost(null);
    };

    const handleUploaderClick = (uploaderId) => {
        navigate(`/profile/${uploaderId}`);
    };

    const getMediaUrl = (filePath) => {
        if (!filePath) return '';
        if (filePath.startsWith('http')) return filePath;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${backendUrl}${filePath}`;
    };

    const getProductImageUrl = (photo) => {
        if (!photo) return '';
        if (photo.startsWith('http')) return photo;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${backendUrl}${photo}`;
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                    <CircularProgress size={40} className="glass-loading" />
                    <Typography variant="body1" className="glass-text-secondary">
                        Loading pending posts...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
            {/* Header */}
            <Box sx={{ mb: 3, textAlign: 'center' }} className="glass-content-container">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    <AssignmentIcon sx={{ color: '#bc965e', fontSize: 32 }} />
                    <Typography variant="h4" className="glass-title">
                        Pending Requests
                    </Typography>
                </Box>
                <Typography variant="body1" className="glass-text-secondary">
                    Review and approve customer posts
                </Typography>
                <Chip 
                    label={`${pendingPosts.length} Pending`} 
                    sx={{ 
                        mt: 1,
                        backgroundColor: 'rgba(255, 152, 0, 0.1)',
                        color: '#ff9800',
                        fontWeight: 600
                    }} 
                />
            </Box>

            {/* Pending Posts Grid */}
            {pendingPosts.length === 0 ? (
                <Paper elevation={1} className="glass-empty-state">
                    <Typography variant="h6" className="glass-text-secondary" gutterBottom>
                        No pending posts
                    </Typography>
                    <Typography variant="body2" className="glass-text-secondary">
                        All customer posts have been reviewed!
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {pendingPosts.map((post) => {
                        const isVideo = post.ContentType === 'video';
                        const mediaUrl = getMediaUrl(post.FilePath);
                        const timeAgo = formatDistanceToNow(new Date(post.CreatedAt), { addSuffix: true });
                        
                        return (
                            <Grid item xs={12} sm={6} md={4} key={post.ContentId}>
                                <Card className="glass-card" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {/* Media */}
                                    <Box 
                                        sx={{ 
                                            position: 'relative', 
                                            paddingTop: '100%',
                                            cursor: 'pointer',
                                            overflow: 'hidden'
                                        }}
                                        onClick={() => handlePostClick(post)}
                                    >
                                        {isVideo ? (
                                            <CardMedia
                                                component="video"
                                                src={mediaUrl}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        ) : (
                                            <CardMedia
                                                component="img"
                                                image={mediaUrl}
                                                alt={post.Title}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Content */}
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" className="glass-text-primary" gutterBottom noWrap>
                                            {post.Title}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            className="glass-text-secondary" 
                                            sx={{ 
                                                mb: 1,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {post.Caption}
                                        </Typography>

                                        {/* Uploader */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <PersonIcon sx={{ fontSize: 16, color: '#bc965e' }} />
                                            <Typography variant="caption" className="glass-text-secondary">
                                                {post.UploaderName}
                                            </Typography>
                                            <Typography variant="caption" className="glass-text-secondary">
                                                • {timeAgo}
                                            </Typography>
                                        </Box>

                                        {/* Tagged Products */}
                                        {post.AttachedProducts && post.AttachedProducts.length > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                <TagIcon sx={{ fontSize: 14, color: '#bc965e' }} />
                                                {post.AttachedProducts.slice(0, 2).map((product) => (
                                                    <Chip
                                                        key={product.ProductCode}
                                                        avatar={<Avatar src={getProductImageUrl(product.Photo)} />}
                                                        label={product.ProductName}
                                                        size="small"
                                                        sx={{ 
                                                            height: 24,
                                                            fontSize: '0.7rem',
                                                            backgroundColor: 'rgba(188, 150, 94, 0.1)'
                                                        }}
                                                    />
                                                ))}
                                                {post.AttachedProducts.length > 2 && (
                                                    <Typography variant="caption" className="glass-text-secondary">
                                                        +{post.AttachedProducts.length - 2} more
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </CardContent>

                                    {/* Actions */}
                                    <CardActions sx={{ p: 2, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            startIcon={actionLoading === post.ContentId ? <CircularProgress size={16} /> : <ApproveIcon />}
                                            onClick={() => handleApprove(post.ContentId)}
                                            disabled={actionLoading === post.ContentId}
                                            className="glass-button"
                                            sx={{ mr: 1 }}
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<RejectIcon />}
                                            onClick={() => handleReject(post.ContentId)}
                                            disabled={actionLoading === post.ContentId}
                                            className="glass-button-outlined"
                                            sx={{ 
                                                borderColor: '#d32f2f',
                                                color: '#d32f2f',
                                                '&:hover': {
                                                    borderColor: '#d32f2f',
                                                    backgroundColor: 'rgba(211, 47, 47, 0.1)'
                                                }
                                            }}
                                        >
                                            Reject
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Post Detail Modal */}
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            maxHeight: { xs: '100vh', sm: '90vh' },
                            m: { xs: 0, sm: 2 }
                        }
                    }
                }}
            >
                <Box sx={{ position: 'relative', height: '100%' }}>
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            top: { xs: 8, sm: 16 },
                            right: { xs: 8, sm: 16 },
                            zIndex: 1300,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {selectedPost && (
                        <Box sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: { xs: 0, sm: 2 }
                        }}>
                            <Box sx={{ 
                                width: '100%',
                                maxWidth: { xs: '100%', sm: '425px' }
                            }}>
                                <ContentCard
                                    content={selectedPost}
                                    onLike={() => {}}
                                    onComment={() => {}}
                                    onShare={() => {}}
                                    currentUser={user}
                                    onUploaderClick={handleUploaderClick}
                                    onDelete={() => {}}
                                />
                                
                                {/* Approval Actions in Modal */}
                                <Box sx={{ display: 'flex', gap: 2, mt: 2, px: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={actionLoading === selectedPost.ContentId ? <CircularProgress size={16} /> : <ApproveIcon />}
                                        onClick={() => handleApprove(selectedPost.ContentId)}
                                        disabled={actionLoading === selectedPost.ContentId}
                                        className="glass-button"
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<RejectIcon />}
                                        onClick={() => handleReject(selectedPost.ContentId)}
                                        disabled={actionLoading === selectedPost.ContentId}
                                        className="glass-button-outlined"
                                        sx={{ 
                                            borderColor: '#d32f2f',
                                            color: '#d32f2f',
                                            '&:hover': {
                                                borderColor: '#d32f2f',
                                                backgroundColor: 'rgba(211, 47, 47, 0.1)'
                                            }
                                        }}
                                    >
                                        Reject
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Dialog>
        </Container>
    );
};

export default AdminRequests;
